// /app/test/:testId — the V2 test runner (BRIEFS C7.4, design _v2/design/Question.dc.html,
// which mirrors pages/student/TakeTest.js). Full-screen layout (no menu).
//
// - Dark top bar: title, "N answered · M remaining", Questions (show/hide the map), Submit.
//   Practice: a clock that counts UP (no time limit, no auto-submit). Chapter Test (C11.1):
//   NOT timed — no clock; "Saved after every answer · Leave any time" and a Leave button that
//   goes Home without the finish dialog (the test stays open; Home shows "Continue your
//   Chapter Test"). The seconds per question are still counted and saved with each answer.
// - "Q 12345" (questions.id) top-right on the question card (C11.1).
// - Practice: an answer is saved at once and may be changed or cleared until Submit.
// - Chapter: an answer is graded at once and locked; the screen shows right/wrong, the
//   answer panel (C11.2: correct answer, explanation, every notes reference as a button
//   into the notes with a Back button) and "This question: seen N times · correct X · wrong Y".
//   Practice shows none of this until the result page.
// - Everything lives on the server, so leaving and coming back resumes the test (the
//   question he was on is remembered on this computer).
// Events: question_show (one per showing, with how long it was shown), answer,
// answer_change, test_finish (test_start is sent by the Start buttons).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { finishTest, getTestPaper, saveTestAnswer } from '../../api/v2';
import { track, useActivityArea } from '../../components/app/activity';
import { IconClock } from '../../components/app/icons';
import {
  AnswerPanel, ConfirmDialog, LETTERS, OptionRow, POINT_CSS, PyqBadge, QId, QuestionBody, card, clock, errorText,
  plural, refHref, revealedState, testPosKey,
} from '../../components/app/testParts';
import { C, FONT, button } from '../../components/app/theme';
import { Loading } from '../../components/app/LoadState';

const POS_KEY = testPosKey;

function readPos(id) {
  try { const n = Number(localStorage.getItem(POS_KEY(id))); return Number.isInteger(n) ? n : null; } catch { return null; }
}
function writePos(id, n) {
  try { localStorage.setItem(POS_KEY(id), String(n)); } catch { /* ignore */ }
}
function dropPos(id) {
  try { localStorage.removeItem(POS_KEY(id)); } catch { /* ignore */ }
}

const barBtn = {
  height: 44, padding: '0 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)',
  background: 'transparent', color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 13, cursor: 'pointer',
};

// ------------------------------------------------------------------ the map

function mapLook(q, chapter) {
  if (chapter && q.revealed) {
    return q.revealed.correct ? { bg: C.goodBg, label: 'right' } : { bg: '#FEE2E2', label: 'wrong' };
  }
  if (!chapter && q.your_answer) return { bg: C.tint, label: 'answered' };
  return { bg: C.bg, label: 'not answered' };
}

function QuestionMap({ questions, current, onPick, chapter, answered }) {
  return (
    <div data-testid="v2-qmap" style={{ ...card, padding: 16, position: 'sticky', top: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Question Map</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 4 }}>
        {questions.map((q, i) => {
          const look = mapLook(q, chapter);
          const on = i === current;
          return (
            <button key={q.question_id} type="button" onClick={() => onPick(i)}
              data-testid={`v2-qmap-${i + 1}`} data-look={look.label}
              aria-label={`Question ${i + 1}, ${look.label}`}
              style={{
                height: 34, borderRadius: 6, border: on ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                background: look.bg, color: on ? C.primary : '#334155', fontFamily: FONT.ui, fontSize: 12,
                fontWeight: on ? 700 : 400, cursor: 'pointer', padding: 0,
              }}>{i + 1}</button>
          );
        })}
      </div>
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
        <div>Answered: <strong data-testid="v2-map-answered">{answered}</strong></div>
        <div>Remaining: <strong>{questions.length - answered}</strong></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: C.muted }}>
          {chapter ? (
            <>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: C.goodBg, border: '1px solid #6EE7B7' }} />Right
              <span style={{ width: 12, height: 12, marginLeft: 8, borderRadius: 3, background: '#FEE2E2', border: '1px solid #FCA5A5' }} />Wrong
            </>
          ) : (
            <>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: C.tint, border: '1px solid #C7D2FE' }} />Answered
              <span style={{ width: 12, height: 12, marginLeft: 8, borderRadius: 3, background: C.bg, border: `1px solid ${C.border}` }} />Not answered
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ page

export default function AppTest() {
  const { testId } = useParams();
  useActivityArea('test');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const paperQ = useQuery({
    queryKey: ['v2', 'test', testId],
    queryFn: () => getTestPaper(testId),
    refetchOnWindowFocus: false,
    // Coming back (e.g. from the notes, C7.5) must show the answers given since the paper
    // was cached: always load it again and wait for that fresh copy.
    refetchOnMount: 'always',
    retry: (n, err) => !(err && err.response && err.response.status === 404) && n < 2,
  });

  const [qs, setQs] = useState(null);          // local copy of the questions (answers change)
  const [current, setCurrent] = useState(0);
  const [showMap, setShowMap] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [asking, setAsking] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const qTime = useRef({});                    // question_id → seconds spent on it
  const shown = useRef(null);                  // {qid, number, at, ms}

  const paper = paperQ.isFetchedAfterMount ? paperQ.data : undefined;
  const chapter = paper && paper.mode === 'v2_chapter';

  // First load: copy the questions, find where he was.
  useEffect(() => {
    if (!paper || qs) return;
    const list = paper.questions || [];
    setQs(list);
    const times = {};
    let total = 0;
    list.forEach((q) => { times[q.question_id] = q.time_spent_seconds || 0; total += q.time_spent_seconds || 0; });
    qTime.current = times;
    setElapsed(total);
    const saved = readPos(testId);
    const firstOpen = list.findIndex((q) => (chapter ? !q.revealed : !q.your_answer));
    const start = saved !== null && saved >= 0 && saved < list.length ? saved : Math.max(0, firstOpen);
    setCurrent(start);
  }, [paper, qs, testId, chapter]);

  // The clock counts up while the page is in view; the time also goes to the question shown.
  useEffect(() => {
    if (!qs) return undefined;
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setElapsed((s) => s + 1);
      const q = qs[current];
      if (q) qTime.current[q.question_id] = (qTime.current[q.question_id] || 0) + 1;
    }, 1000);
    return () => clearInterval(t);
  }, [qs, current]);

  // question_show: one event per showing, sent when he leaves it (with how long it was shown).
  const endShow = useCallback(() => {
    const s = shown.current;
    if (!s) return;
    shown.current = null;
    track('question_show', {
      at: new Date(s.at).toISOString(), test_id: testId, question_id: s.qid,
      duration_ms: Math.max(0, Date.now() - s.at), details: { number: s.number },
    });
  }, [testId]);

  useEffect(() => {
    if (!qs || !qs[current]) return undefined;
    shown.current = { qid: qs[current].question_id, number: current + 1, at: Date.now() };
    writePos(testId, current);
    return endShow;
  }, [qs && qs.length, current, testId, endShow]);

  const answered = useMemo(() => (qs ? qs.filter((q) => q.your_answer).length : 0), [qs]);

  if (paperQ.isError) {
    const gone = paperQ.error && paperQ.error.response && paperQ.error.response.status === 404;
    return (
      <div style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 560 }}>
        <div style={{ fontFamily: FONT.heading, fontSize: 22, fontWeight: 600 }}>{gone ? 'This test was not found.' : 'The test could not load.'}</div>
        {!gone && <div style={{ fontSize: 15, color: C.muted }}>Check the internet and press Try again.</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          {!gone && <button type="button" style={button.primary} onClick={() => paperQ.refetch()}>Try again</button>}
          <button type="button" style={button.secondary} onClick={() => navigate('/app')}>Back to Home</button>
        </div>
      </div>
    );
  }
  if (!paper || !qs) return <Loading text="Loading your test…" style={{ padding: 32 }} />;
  if (paper.status === 'completed') return <Navigate to={`/app/test/${testId}/result`} replace />;
  if (qs.length === 0) return <Navigate to="/app" replace />;

  const q = qs[Math.min(current, qs.length - 1)];
  const total = qs.length;
  const remaining = total - answered;
  const revealed = chapter ? q.revealed : null;

  const patch = (qid, fields) => setQs((list) => list.map((x) => (x.question_id === qid ? { ...x, ...fields } : x)));

  async function choose(letter) {
    if (saving || finishing) return;
    setError('');
    const before = q.your_answer || null;
    if (chapter) {
      if (q.revealed) return;
      setSaving(true);
      patch(q.question_id, { your_answer: letter });
      try {
        const res = await saveTestAnswer(testId, q.question_id, letter, qTime.current[q.question_id] || 0);
        patch(q.question_id, {
          your_answer: res.your_answer,
          revealed: {
            correct: res.correct, correct_option: res.correct_option, your_answer: res.your_answer,
            notes_target: res.notes_target, stat: res.stat,
            explanation: res.explanation, explanation_image_url: res.explanation_image_url,
            notes_refs: res.notes_refs || [],
          },
        });
        track('answer', { test_id: testId, question_id: q.question_id, details: { number: current + 1, answer: letter, correct: res.correct, mode: 'v2_chapter' } });
      } catch (err) {
        if (err && err.response && err.response.status === 409) {
          // already answered (another window?) — take the server's copy
          const fresh = await getTestPaper(testId).catch(() => null);
          if (fresh) setQs(fresh.questions || []);
        } else {
          patch(q.question_id, { your_answer: before });
          setError(errorText(err, 'Your answer could not be saved. Please try again.'));
        }
      } finally {
        setSaving(false);
      }
      return;
    }
    // practice: save; same letter again does nothing
    if (before === letter) return;
    patch(q.question_id, { your_answer: letter });
    try {
      await saveTestAnswer(testId, q.question_id, letter, qTime.current[q.question_id] || 0);
      track(before ? 'answer_change' : 'answer', {
        test_id: testId, question_id: q.question_id,
        details: { number: current + 1, answer: letter, before: before || undefined, mode: 'v2_practice' },
      });
    } catch (err) {
      patch(q.question_id, { your_answer: before });
      setError(errorText(err, 'Your answer could not be saved. Please try again.'));
    }
  }

  async function clearAnswer() {
    const before = q.your_answer;
    if (chapter || !before) return;
    setError('');
    patch(q.question_id, { your_answer: null });
    try {
      await saveTestAnswer(testId, q.question_id, null, qTime.current[q.question_id] || 0);
      track('answer_change', { test_id: testId, question_id: q.question_id, details: { number: current + 1, answer: null, before, mode: 'v2_practice' } });
    } catch (err) {
      patch(q.question_id, { your_answer: before });
      setError(errorText(err, 'Your answer could not be cleared. Please try again.'));
    }
  }

  async function finish() {
    setFinishing(true);
    setError('');
    endShow();
    try {
      const res = await finishTest(testId);
      track('test_finish', {
        test_id: testId,
        duration_ms: elapsed * 1000,
        details: {
          mode: paper.mode, total: res.total, attempted: res.attempted, correct: res.correct,
          incorrect: res.incorrect, score: res.score, max_score: res.max_score,
        },
      });
      dropPos(testId);
      qc.invalidateQueries({ queryKey: ['v2', 'open-test'] });
      qc.invalidateQueries({ queryKey: ['v2', 'dashboard'] });
      qc.removeQueries({ queryKey: ['v2', 'test', testId] });
      navigate(`/app/test/${testId}/result`, { replace: true });
    } catch (err) {
      setError(errorText(err, 'The test could not be finished. Please try again.'));
      setFinishing(false);
      setAsking(false);
    }
  }

  // Chapter Test (C11.1): leave any time — every answer is already saved on the server.
  // (No new event type: the question_show sent by endShow() records where he stopped.)
  function leave() {
    endShow();
    qc.invalidateQueries({ queryKey: ['v2', 'open-test'] });
    qc.invalidateQueries({ queryKey: ['v2', 'dashboard'] });
    navigate('/app');
  }

  const go = (i) => setCurrent(Math.max(0, Math.min(total - 1, i)));

  const optionState = (letter) => {
    if (revealed) return revealedState(letter, revealed.your_answer, revealed.correct_option);
    return q.your_answer === letter ? 'chosen' : 'plain';
  };

  const stat = revealed && revealed.stat;

  return (
    <div data-testid="v2-test" data-mode={paper.mode}>
      <style>{POINT_CSS}</style>
      <div style={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, padding: '10px 20px', background: C.bar, color: '#FFFFFF', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ minWidth: 0 }}>
          <div data-testid="v2-test-title" style={{ fontFamily: FONT.heading, fontWeight: 700, fontSize: 15 }}>{paper.title}</div>
          {chapter && (
            <div data-testid="v2-test-saved" style={{ fontSize: 13, color: '#FFFFFF' }}>Saved after every answer · Leave any time</div>
          )}
          <div data-testid="v2-test-counts" style={{ fontSize: 12, color: '#C7D2FE' }}>{answered} answered · {remaining} remaining</div>
        </div>
        {!chapter && (
          <div data-testid="v2-test-clock" title="Time on this test" style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: C.bg,
            border: '2px solid #10B981', borderRadius: 8,
          }}>
            <IconClock stroke={C.good} />
            <span style={{ fontFamily: FONT.heading, fontWeight: 800, fontSize: 20, color: C.good }}>{clock(elapsed)}</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          {chapter && (
            <button type="button" data-testid="v2-leave" onClick={leave} disabled={finishing} style={barBtn}>Leave</button>
          )}
          <button type="button" data-testid="v2-toggle-map" aria-pressed={showMap} onClick={() => setShowMap((s) => !s)} style={barBtn}>Questions</button>
          <button type="button" data-testid="v2-submit" onClick={() => setAsking(true)} disabled={finishing}
            style={{ ...barBtn, padding: '0 20px', border: 0, background: C.good, fontWeight: 700, fontSize: 14 }}>
            {finishing ? 'Finishing…' : 'Submit'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 20, maxWidth: 1100, margin: '0 auto', padding: 20, boxSizing: 'border-box' }}>
        <div style={{ flex: '999 1 480px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span data-testid="v2-q-number" style={{ fontSize: 13, color: '#64748B' }}>Question {current + 1} of {total}</span>
            <div style={{ flex: '1 1 0', height: 4, background: C.border, borderRadius: 2 }}>
              <div style={{ width: `${((current + 1) / total) * 100}%`, height: 4, borderRadius: 2, background: C.primary, transition: 'width 0.3s' }} />
            </div>
          </div>

          <div data-testid="v2-q-card" style={{ ...card, position: 'relative', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <QId id={q.question_id} style={{ position: 'absolute', top: 6, right: 14 }} />
            <PyqBadge pyq={q.pyq} style={{ marginBottom: -8 }} />
            <QuestionBody q={q} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {LETTERS.map((L) => (
                <OptionRow key={L} letter={L} html={q[`option_${L.toLowerCase()}`]}
                  imageUrl={q[`option_${L.toLowerCase()}_image_url`]} state={optionState(L)}
                  disabled={!!revealed || saving || finishing} onClick={() => choose(L)} />
              ))}
            </div>

            {chapter && saving && !revealed && <div style={{ fontSize: 14, color: C.muted }}>Checking your answer…</div>}
            {error && <div data-testid="v2-test-error" style={{ fontSize: 14, color: C.bad }}>{error}</div>}

            {revealed && (
              <AnswerPanel correctOption={revealed.correct_option} explanation={revealed.explanation}
                imageUrl={revealed.explanation_image_url} refs={revealed.notes_refs}
                pointHtml={revealed.notes_target && revealed.notes_target.point_html}
                hrefFor={(ref) => refHref(ref, { testId, number: current + 1 })}
                waitNote="Your test will wait. A Back button in the notes brings you here again." />
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ minHeight: 20 }}>
                {stat && (
                  <div data-testid="v2-q-stat" style={{ fontSize: 12, color: '#64748B' }}>
                    This question: seen {plural(stat.seen, 'time', 'times')} · correct {stat.correct} · wrong {stat.incorrect}
                  </div>
                )}
                {!chapter && q.your_answer && (
                  <button type="button" data-testid="v2-clear" onClick={clearAnswer} style={{
                    minHeight: 44, padding: '0 14px', borderRadius: 8, border: 0, background: 'transparent',
                    color: C.muted, fontFamily: FONT.ui, fontSize: 14, cursor: 'pointer', textDecoration: 'underline',
                  }}>Clear my answer</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" data-testid="v2-prev" disabled={current === 0} onClick={() => go(current - 1)} style={{
                  height: 44, padding: '0 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent',
                  color: C.muted, fontFamily: FONT.ui, fontSize: 14, fontWeight: 500,
                  cursor: current === 0 ? 'default' : 'pointer', opacity: current === 0 ? 0.5 : 1,
                }}>Prev</button>
                {current < total - 1 ? (
                  <button type="button" data-testid="v2-next" onClick={() => go(current + 1)} style={{
                    height: 44, padding: '0 18px', borderRadius: 8, border: 0, background: C.primary,
                    color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 14, fontWeight: 500, cursor: 'pointer',
                  }}>Next</button>
                ) : (
                  <button type="button" data-testid="v2-last-submit" onClick={() => setAsking(true)} style={{
                    height: 44, padding: '0 18px', borderRadius: 8, border: 0, background: C.good,
                    color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  }}>Submit</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {showMap && (
          <div style={{ flex: '1 1 220px', maxWidth: 240 }}>
            <QuestionMap questions={qs} current={current} onPick={go} chapter={chapter} answered={answered} />
          </div>
        )}
      </div>

      {asking && (
        <ConfirmDialog testId="v2-finish-confirm" title="Finish the test?"
          text={remaining > 0 ? `${plural(remaining, 'question', 'questions')} not answered.` : 'You answered every question.'}
          yes="Yes, finish" no="Not yet" busy={finishing} onYes={finish} onNo={() => setAsking(false)} />
      )}
    </div>
  );
}
